import { format, isValid } from "date-fns";
import i18n from "../../i18n";


export const TENANT_REQUEST_STATUSES = ["Open", "In_Progress", "Onboarded", "Closed"];

/** What the list shows, in the order a person reads it. */
export const LIST_FIELDS = [
  "organizationName",
  "natureOfBusiness",
  "officePhoneNumber",
  "gstRegistrationType",
];

/** The one field the search matches on. */
export const SEARCH_FIELD = "organizationName";

/** Shown in the card's own header, so not repeated in its body. */
const HEADER_FIELDS = ["orgId", "organizationName", "status", "createdAt"];

/** The fields that carry a timestamp rather than text. */
const DATE_FIELDS = ["createdAt", "updatedAt"];

/**
 * Labels.
 *
 * Only the named few need one: the card's fields are the API's own, too many
 * and too changeable to name, so each falls back to its key made readable.
 */
const LABEL_KEYS = {
  organizationName: "tenantRequests.organization",
  natureOfBusiness: "tenantRequests.natureOfBusiness",
  officePhoneNumber: "common.phone",
  gstRegistrationType: "tenantRequests.gstRegistrationType",
  createdAt: "tenantRequests.requestedOn",
};

function humanise(key) {
  return String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (first) => first.toUpperCase());
}

export function fieldLabel(key) {
  const translationKey = LABEL_KEYS[key];
  if (!translationKey) return humanise(key);
  const label = i18n.t(translationKey);
  return label === translationKey ? humanise(key) : label;
}

/**
 * A field as it is shown.
 *
 * A date is stamped the way the rest of the app stamps one; anything else is
 * whatever came back, and an empty field is empty rather than "null".
 */
export function fieldValue(request, key) {
  const raw = request?.[key];
  if (raw === null || raw === undefined || String(raw).trim() === "") return "";
  if (!DATE_FIELDS.includes(key)) return String(raw);

  const date = new Date(raw);
  return isValid(date) ? format(date, "dd MMM yyyy") : String(raw);
}

/**
 * Everything else the request carries, in the order the response gives it.
 *
 * Taken from the row rather than from a list of names: the profile is the
 * organization's own answers, and one left blank is dropped instead of filling
 * the card with dashes.
 */
function answeredFields(request) {
  return Object.keys(request ?? {}).filter(
    (key) => !HEADER_FIELDS.includes(key) && fieldValue(request, key) !== ""
  );
}

/**
 * How the card reads: the profile in the groups a reviewer works through, each
 * under its own heading.
 *
 * A field the form starts collecting tomorrow is not lost for want of a home
 * here — whatever no group claims falls to the last one, so the card still
 * shows the whole of what was answered.
 */
const SECTIONS = [
  {
    titleKey: "tenantRequests.sectionOrganization",
    fields: [
      "tradeName",
      "natureOfBusiness",
      "industry",
      "productsServicesOffered",
      "yearOfEstablishment",
      "constitutionOfBusiness",
      "website",
      "companyLogoUrl",
    ],
  },
  {
    titleKey: "tenantRequests.sectionTax",
    fields: [
      "gstin",
      "pan",
      "udyamRegistrationNumber",
      "cinLlpin",
      "tan",
      "gstRegistrationType",
      "gstState",
      "registeredOfficeAddress",
      "registeredOfficeCity",
      "registeredOfficeState",
      "registeredOfficePinCode",
      "registeredOfficeCountry",
    ],
  },
  {
    titleKey: "tenantRequests.sectionTeam",
    fields: [
      "totalNumberOfEmployees",
      "numberOfBranches",
      "numberOfOfficeLocations",
      "numberOfTeams",
      "departments",
    ],
  },
  {
    titleKey: "tenantRequests.sectionPolicy",
    fields: [
      "payrollCycle",
      "salaryDisbursementDate",
      "officeTimings",
      "shiftTimings",
      "weeklyOffPattern",
      "holidayList",
      "leavePolicy",
      "leaveEligibilityRules",
      "attendanceCaptureMethod",
    ],
  },
  {
    titleKey: "tenantRequests.sectionApplications",
    fields: [
      "existingAccountingSoftware",
      "existingPayrollSoftware",
      "existingHrms",
      "existingAttendanceSystem",
      "existingErp",
      "existingCrm",
    ],
  },
  {
    titleKey: "tenantRequests.sectionContact",
    fields: [
      "officePhoneNumber",
      "officialEmailAddress",
      "ownerName",
      "ownerEmail",
      "ownerPhone",
      "hrPocName",
      "hrPocEmail",
      "hrPocPhone",
    ],
  },
];

const CLAIMED_FIELDS = SECTIONS.flatMap((section) => section.fields);

/** The card's groups, with the empty ones left out. */
export function detailSections(request) {
  const answered = answeredFields(request);
  const shown = new Set(answered);

  const sections = SECTIONS.map((section) => ({
    title: i18n.t(section.titleKey),
    fields: section.fields.filter((key) => shown.has(key)),
  }));

  const unclaimed = answered.filter((key) => !CLAIMED_FIELDS.includes(key));
  if (unclaimed.length) {
    sections.push({ title: i18n.t("tenantRequests.sectionOther"), fields: unclaimed });
  }

  return sections.filter((section) => section.fields.length > 0);
}

/** The id the status endpoint is keyed by. */
export function tenantRequestId(request) {
  return request?.orgId ?? request?.id ?? "";
}

/** The status, whichever name the response gives the field. */
export function tenantRequestStatusOf(request) {
  return request?.status ?? request?.requestStatus ?? "";
}

/** "In_Progress" reads as "In Progress" — the underscore is the API's. */
export function statusLabel(status) {
  return String(status ?? "").replace(/[_-]+/g, " ");
}

/**
 * The statuses a request may be moved to.
 *
 * A status the API answered with but does not list stays at the front of its
 * own dropdown: dropping it would leave the control blank and make the request
 * look as though it had no status at all.
 */
export function statusOptions(current) {
  const chosen = String(current ?? "").trim();
  return chosen && !TENANT_REQUEST_STATUSES.includes(chosen)
    ? [chosen, ...TENANT_REQUEST_STATUSES]
    : TENANT_REQUEST_STATUSES;
}
